import {
  EventRecordDto,
  InfantDto,
  VisitDataDto,
  VisitDto,
} from '@ecdlink/core';
import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import localForage from 'localforage';
import { ThunkStateStatus } from '../types';
import { setFulfilledThunkActionStatus, setThunkActionStatus } from '../utils';
import { v4 as uuidv4 } from 'uuid';
import {
  getInfants,
  addInfant,
  getInfantVisits,
  updateInfant,
  updateInfantCaregiverAddress,
  updateInfantCaregiverContactDetails,
  getAllInfantEventRecordTypes,
  getEventRecordsByClientId,
  updateInfantCaregiver,
  addAdditionalVisitForInfant,
  getReferralsForInfant,
  getCompletedReferralsForInfant,
  updateVisitDataStatus,
  getInfantStatusInfoForIds,
  getAllVisitsForInfantIds,
  syncInfantVisits,
  syncInfantEventRecords,
  upsertInfants,
} from './infant.actions';
import { InfantState } from './infant.types';
import { eventNames } from '@/pages/infant/infant-profile/visits-tab/record-event/index.types';

const initialState: InfantState & ThunkStateStatus = {
  status: [],
};

const infantSlice = createSlice({
  name: 'infant',
  initialState,
  reducers: {
    resetInfantState: (state) => {
      state.infants = initialState.infants;
    },
    clearSyncStatus: (state) => {
      if (state.infants) {
        for (let i = 0; i < state.infants.length; i++) {
          if (
            'synced' in state.infants[i] &&
            state.infants[i].synced === false
          ) {
            state.infants[i] = {
              ...state.infants[i],
              synced: true,
            };
          }
        }
      }
    },
    setInfantTabClicked: (
      state,
      action: PayloadAction<{
        infantId: string;
        tab: 'visits' | 'progress' | 'referrals' | 'contact';
        clicked: boolean;
        isOnline: boolean;
      }>
    ) => {
      const { infantId, tab, clicked, isOnline } = action.payload;
      const infant = state.infants?.find(
        (i) => i.id === infantId || i.userId === infantId
      );
      if (!infant) return;

      // Only act when we're being told the tab was actually viewed/clicked
      if (!clicked) return; // ← explicit: ignore "unclick" events

      let shouldMarkSynced = false;

      switch (tab) {
        case 'visits':
          if (!infant.clickedVisitTab) {
            infant.clickedVisitTab = true;
            shouldMarkSynced = true;
          }
          break;

        case 'progress':
          if (!infant.clickedProgressTab) {
            infant.clickedProgressTab = true;
            shouldMarkSynced = true;
          }
          break;

        case 'referrals':
          if (!infant.clickedReferralsTab) {
            infant.clickedReferralsTab = true;
            shouldMarkSynced = true;
          }
          break;

        case 'contact':
          if (!infant.clickedContactTab) {
            infant.clickedContactTab = true;
            shouldMarkSynced = true;
          }
          break;
      }

      if (shouldMarkSynced) {
        infant.synced = isOnline;
      }
    },
    addEventRecordToInfant: (state, action: PayloadAction<EventRecordDto>) => {
      if (state.infants) {
        const infant = state.infants.find(
          (x) =>
            x.id === action.payload.infantId ||
            x.userId === action.payload.infantId
        );

        if (infant) {
          // Ensure eventRecords is initialized if it's undefined
          if (!infant.eventRecords) {
            infant.eventRecords = [];
          }

          // Add the new event record
          infant.eventRecords.push(action.payload);

          // Check if the event type is one of the special types
          const specialEvents = [
            eventNames.caregiverIsPregnant,
            eventNames.newChildInFamily,
            eventNames.caregiverHasChanged,
          ];

          const isSpecial = specialEvents.includes(
            action.payload.eventRecordTypeName!
          );

          infant.isActive = isSpecial; // don't archive when it is one of the 3 events
          infant.synced = isSpecial; // Assuming this is the intended behavior
        }
      }
    },
    addInfant: (state, action: PayloadAction<InfantDto>) => {
      if (!state.infants) state.infants = [];
      state.infants?.push(action.payload);
    },
    restartInfantVisit: (
      state,
      action: PayloadAction<{
        infantId: string;
        visitId: string;
      }>
    ) => {
      const { infantId, visitId } = action.payload;

      const infant = state.infants?.find((x) => x.id === infantId);
      if (!infant) return;

      const visits = infant.visits || [];
      const now = new Date().toISOString();

      const existingVisitIndex = visits.findIndex((v) => v.id === visitId);
      if (existingVisitIndex === -1) return;

      const existingVisit = visits[existingVisitIndex];

      // Cancel current visit
      const updatedVisit = {
        ...existingVisit,
        isCancelled: true,
        synced: false,
      };

      // Create new visit
      const newVisit: VisitDto = {
        id: uuidv4(),
        dueDate: existingVisit.dueDate ?? null,
        actualVisitDate: existingVisit.actualVisitDate ?? null,
        attended: false,
        isCancelled: false,
        startedDate: '',
        plannedVisitDate: existingVisit.plannedVisitDate ?? null,
        orderDate: existingVisit.orderDate ?? null,
        visitType: existingVisit.visitType,
        insertedDate: now,
        clientId: existingVisit.clientId,
        linkedVisitId: existingVisit.id,
        synced: false,
        visitAnswers: [],
      };

      // Update visits array
      if (state.infants) {
        state.infants = state.infants.map((m) =>
          m.id === infantId
            ? {
                ...m,
                visits: [
                  ...visits.slice(0, existingVisitIndex),
                  updatedVisit,
                  newVisit,
                  ...visits.slice(existingVisitIndex + 1),
                ],
              }
            : m
        );
      }
    },
    addInfantAdditionalVisit: (state, action: PayloadAction<VisitDto>) => {
      if (!state.infants) return;

      const infantIndex = state.infants.findIndex(
        (m) => m.id === action.payload.clientId
      );
      if (infantIndex === -1) return;

      const updatedInfant = {
        ...state.infants[infantIndex],
        visits: [
          ...(state.infants[infantIndex].visits || []), // Spread existing visits or start with an empty array
          action.payload, // Add the new visit
        ],
      };

      // Update the state.mothers array immutably
      state.infants = state.infants.map((m, index) =>
        index === infantIndex ? updatedInfant : m
      );
    },
    addInfantVisits: (state, action: PayloadAction<VisitDto[]>) => {
      action.payload.forEach((visit) => {
        const infantId = visit.clientId;
        if (!infantId) return;

        const infant = state.infants?.find((m) => m.id === infantId);
        if (!infant) return;

        if (!infant.visits) {
          infant.visits = [];
        }

        const existingIndex = infant.visits.findIndex((v) => v.id === visit.id);

        const normalizedVisit = { ...visit, clientId: infantId };

        if (existingIndex === -1) {
          infant.visits.push(normalizedVisit);
        } else {
          infant.visits[existingIndex] = {
            ...infant.visits[existingIndex],
            ...visit,
            clientId: infantId,
          };
        }
      });
    },
    updateInfantVisit: (
      state,
      action: PayloadAction<{
        infantId: string;
        visitId: string;
        visitAnswers: VisitDataDto[];
        visitName: string;
        isOnline: boolean;
      }>
    ) => {
      const { infantId, visitId, visitAnswers, visitName, isOnline } =
        action.payload;

      if (!state.infants) return;

      const infantIndex = state.infants.findIndex((m) => m.id === infantId);
      if (infantIndex === -1) return;

      const infant = state.infants[infantIndex];
      if (!infant.visits) return;

      const visitIndex = infant.visits.findIndex((v) => v.id === visitId);
      if (visitIndex === -1) return;

      const visit = infant.visits[visitIndex];
      const existingAnswers = visit.visitAnswers ?? [];

      const now = new Date().toISOString();
      const isFollowUp = visitName === 'Follow up';

      // ────────────────────────────────────────────────
      // Build answer map for merging (same as before)
      // ────────────────────────────────────────────────
      const answerMap = new Map<string, VisitDataDto>();

      existingAnswers.forEach((ans) => {
        const key = `${ans.visitName}|${ans.visitSection}|${ans.question}`;
        answerMap.set(key, ans);
      });

      // Merge new answers (will be empty if no questions)
      visitAnswers.forEach((newAns) => {
        const key = `${newAns.visitName}|${newAns.visitSection}|${newAns.question}`;
        const existing = answerMap.get(key);
        answerMap.set(key, { ...(existing ?? {}), ...newAns });
      });

      // If the incoming payload represents a screen with no questions,
      // we still want to record that the section was visited.
      // We can do this by checking if visitAnswers came from a known empty section.
      // Most reliable way → rely on visitName + visitSection being present even when empty

      // Update the visit
      infant.visits[visitIndex] = {
        ...visit,
        visitAnswers: Array.from(answerMap.values()),
        startedDate:
          !visit.startedDate || visit.startedDate === ''
            ? now
            : visit.startedDate,
        attended: isFollowUp ? true : visit.attended,
        actualVisitDate: isFollowUp ? now : visit.actualVisitDate,
        synced: isOnline,
      };
    },
    updateInfantStatusInfo: (state, action: PayloadAction<InfantDto>) => {
      if (!state.infants) return;

      const index = state.infants.findIndex(
        (infant) => infant.id === action.payload.id
      );
      if (index === -1) return;

      state.infants[index].statusInfo = action.payload.statusInfo;
    },
    updateInfant: (state, action: PayloadAction<InfantDto>) => {
      if (!state.infants) return;

      const index = state.infants.findIndex(
        (infant) => infant.id === action.payload.id
      );
      if (index === -1) return;

      // Only update statusInfo — ignore all other fields from payload
      state.infants[index].statusInfo = action.payload.statusInfo;
    },
    updateInfantCsgStatus: (
      state,
      action: PayloadAction<{
        infantId: string;
        hasAnsweredNoToCSGReceiving: boolean;
      }>
    ) => {
      const { infantId, hasAnsweredNoToCSGReceiving } = action.payload;

      if (state.infants) {
        for (const element of state.infants) {
          if (element.id === infantId && element.visitInfo) {
            element.visitInfo.hasAnsweredNoToCSGReceiving =
              hasAnsweredNoToCSGReceiving;
            break; // Exit after the first match
          }
        }
      }
    },
    updateInfantRTHDetails: (state, action: PayloadAction<InfantDto>) => {
      if (state.infants) {
        for (const element of state.infants) {
          if (element.id === action.payload.id) {
            element.weightAtBirth = action.payload.weightAtBirth;
            element.lengthAtBirth = action.payload.lengthAtBirth;
          }
        }
      }
    },
  },
  extraReducers: (builder) => {
    setThunkActionStatus(builder, addInfant);
    setThunkActionStatus(builder, updateInfant);
    setThunkActionStatus(builder, getInfants);
    setThunkActionStatus(builder, getInfantStatusInfoForIds);
    setThunkActionStatus(builder, getInfantVisits);
    setThunkActionStatus(builder, getAllVisitsForInfantIds);
    setThunkActionStatus(builder, updateInfantCaregiverAddress);
    setThunkActionStatus(builder, updateInfantCaregiverContactDetails);
    setThunkActionStatus(builder, getAllInfantEventRecordTypes);
    setThunkActionStatus(builder, getEventRecordsByClientId);
    setThunkActionStatus(builder, updateInfantCaregiver);
    setThunkActionStatus(builder, addAdditionalVisitForInfant);
    setThunkActionStatus(builder, getReferralsForInfant);
    setThunkActionStatus(builder, getCompletedReferralsForInfant);
    setThunkActionStatus(builder, updateVisitDataStatus);
    setThunkActionStatus(builder, syncInfantVisits);
    setThunkActionStatus(builder, syncInfantEventRecords);
    setThunkActionStatus(builder, upsertInfants);
    builder.addCase(syncInfantVisits.fulfilled, (state, action) => {
      if (!state.infants) return;

      state.infants = state.infants.map((infant) => ({
        ...infant,
        visits: Array.isArray(infant.visits)
          ? infant.visits.map((visit) => ({
              ...visit,
              synced: visit.synced === false ? true : visit.synced,
            }))
          : infant.visits,
      }));
      setFulfilledThunkActionStatus(state, action);
    });
    builder.addCase(syncInfantEventRecords.fulfilled, (state, action) => {
      if (!state.infants) return;

      state.infants = state.infants.map((infant) => ({
        ...infant,
        eventRecords: Array.isArray(infant.eventRecords)
          ? infant.eventRecords.map((eventRecord) => ({
              ...eventRecord,
              synced: eventRecord.synced === false ? true : eventRecord.synced,
            }))
          : infant.eventRecords,
      }));
      setFulfilledThunkActionStatus(state, action);
    });
    builder.addCase(getInfantStatusInfoForIds.fulfilled, (state, action) => {
      if (!action.payload?.length || !state.infants?.length) {
        setFulfilledThunkActionStatus(state, action);
        return;
      }

      const updates = action.payload;

      state.infants = state.infants.map((infant) => {
        const update = updates.find((u) => u.userId === infant.user?.id);
        return {
          ...infant,
          statusInfo: update?.statusInfo ?? infant.statusInfo,
          visitInfo: update?.visitInfo ?? infant.visitInfo,
        };
      });

      setFulfilledThunkActionStatus(state, action);
    });
    builder.addCase(updateVisitDataStatus.fulfilled, (state, action) => {
      setFulfilledThunkActionStatus(state, action);
    });
    builder.addCase(addInfant.fulfilled, (state, action) => {
      if (state.infants) {
        for (let i = 0; i < state.infants.length; i++) {
          if (
            'synced' in state.infants[i] &&
            state.infants[i].synced === false
          ) {
            state.infants[i] = {
              ...state.infants[i],
              synced: true,
              isNew: false,
            };
          }
        }
      }
      setFulfilledThunkActionStatus(state, action);
    });
    builder.addCase(upsertInfants.fulfilled, (state, action) => {
      if (state.infants) {
        for (let i = 0; i < state.infants.length; i++) {
          if (
            'synced' in state.infants[i] &&
            state.infants[i].synced === false
          ) {
            state.infants[i] = {
              ...state.infants[i],
              synced: true,
              isNew: false,
            };
          }
        }
      }
      setFulfilledThunkActionStatus(state, action);
    });
    builder.addCase(getInfants.fulfilled, (state, action) => {
      // Deep clone + add isActive flag in one go
      const infants = action.payload.map((infant) => ({
        ...infant,
        isActive: true,
      })) as InfantDto[];

      state.infants = infants;
      setFulfilledThunkActionStatus(state, action);
    });
    builder.addCase(getInfantVisits.fulfilled, (state, action) => {
      const infantId = action.meta.arg.infantId;

      // Ensure mothers array exists
      if (!state.infants) {
        state.infants = [];
      }

      const infant = state.infants?.find((m) => m.id === infantId);
      if (!infant) {
        // Optionally handle the case where mother isn't found
        setFulfilledThunkActionStatus(state, action);
        return;
      }

      // Start with existing visits (or empty array)
      const existingVisits = infant.visits || [];

      // Create a map of existing visits for O(1) lookup and update
      const visitMap = new Map(existingVisits.map((v) => [v.id, v]));

      // Merge/update with incoming visits
      action.payload.forEach((visit) => {
        visitMap.set(visit.id, {
          ...visitMap.get(visit.id), // existing fields if any
          ...visit, // new/overwriting fields
          clientId: infantId, // ensure clientId is set
          insertedDate: visit.insertedDate.toString(),
        });
      });

      // Assign the merged visits back (Immer allows this mutation)
      infant.visits = Array.from(visitMap.values());

      setFulfilledThunkActionStatus(state, action);
    });
    builder.addCase(getAllVisitsForInfantIds.fulfilled, (state, action) => {
      // Create a map: infantId → infant object (from actual state)
      const infantMap = new Map(
        (state.infants || []).map((infant) => [infant.id, infant])
      );

      // Process each visit from the payload
      action.payload.forEach((visit) => {
        const infantId = visit.clientId;
        const infant = infantMap.get(infantId);

        if (!infant) {
          return;
        }

        // Ensure visits array exists
        if (!infant.visits) {
          infant.visits = [];
        }

        // Find index of existing visit with same id
        const existingIndex = infant.visits.findIndex((v) => v.id === visit.id);

        const visitWithClientId = { ...visit, clientId: infantId };

        if (existingIndex === -1) {
          // Add new visit
          infant.visits.push(visitWithClientId);
        } else {
          // Update existing visit (merge changes)
          infant.visits[existingIndex] = {
            ...infant.visits[existingIndex],
            ...visit,
            clientId: infantId, // ensure it's preserved
          };
        }
      });
      setFulfilledThunkActionStatus(state, action);
    });
    builder.addCase(updateInfant.fulfilled, (state, action) => {
      if (!action.payload || !state.infants) return;

      state.infants = state.infants?.map((item) => {
        if (item?.user?.id === action.payload?.user?.id) {
          return { ...item, ...action.payload };
        }
        return item;
      });
      setFulfilledThunkActionStatus(state, action);
    });
    builder.addCase(getAllInfantEventRecordTypes.fulfilled, (state, action) => {
      state.eventRecordTypes = action.payload;
      setFulfilledThunkActionStatus(state, action);
    });
    builder.addCase(getEventRecordsByClientId.fulfilled, (state, action) => {
      if (!action.payload || !state.infants) return;
      const clientId = action.meta.arg.clientId;

      // Find and update the infant (mutation is safe in Redux Toolkit with Immer)
      const infant = state.infants.find(
        (x) => x.id === clientId || x.userId === clientId
      );
      if (infant) {
        infant.eventRecords = action.payload as EventRecordDto[];
      }

      // Then update the status (assuming setFulfilledThunkActionStatus mutates state)
      setFulfilledThunkActionStatus(state, action);
    });

    builder.addCase(getReferralsForInfant.fulfilled, (state, action) => {
      state.referralsForInfant = action.payload;
      setFulfilledThunkActionStatus(state, action);
    });
    builder.addCase(
      getCompletedReferralsForInfant.fulfilled,
      (state, action) => {
        state.completedReferralsForInfant = action.payload;
        setFulfilledThunkActionStatus(state, action);
      }
    );
    builder.addCase(updateInfantCaregiver.fulfilled, (state, action) => {
      setFulfilledThunkActionStatus(state, action);
    });
    builder.addCase(addAdditionalVisitForInfant.fulfilled, (state, action) => {
      if (state.visits) {
        state.visits = [...state.visits, action.payload];
      } else {
        state.visits = [action.payload];
      }

      setFulfilledThunkActionStatus(state, action);
    });
  },
});

const { reducer: infantReducer, actions: infantActions } = infantSlice;

const infantPersistConfig = {
  key: 'infant',
  storage: localForage,
  blacklist: [],
};

export { infantPersistConfig, infantReducer, infantActions };
